import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@/src/icons'

import { cn } from '@/registry/reapit/lib/utils'
import { Chip, ChipGroup } from '@/registry/reapit/ui/chip'
import { Button } from '@/registry/reapit/ui/button'
import { TextMuted } from '@/registry/reapit/ui/text'

type GalleryImage = {
  src: string
  alt?: string
  caption?: string
  category?: string
}

type GalleryViewerProps = {
  images: GalleryImage[]
  defaultView?: 'gallery' | 'single'
  defaultCategory?: string
  className?: string
}

function GalleryViewerFilter({
  categories,
  activeCategory,
  onCategoryChange,
}: {
  categories: string[]
  activeCategory: string | null
  onCategoryChange: (category: string | null) => void
}) {
  return (
    <div data-slot="gallery-viewer-filter" className="mb-3">
      <ChipGroup>
        <Chip variant="filter" selected={activeCategory === null} onClick={() => onCategoryChange(null)}>
          All
        </Chip>
        {categories.map((category) => (
          <Chip
            key={category}
            variant="filter"
            selected={activeCategory === category}
            onClick={() => onCategoryChange(category)}
          >
            {category}
          </Chip>
        ))}
      </ChipGroup>
    </div>
  )
}

function GalleryViewerGrid({
  images,
  onImageClick,
}: {
  images: GalleryImage[]
  onImageClick: (index: number) => void
}) {
  return (
    <div data-slot="gallery-viewer-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1">
      {images.map((image, index) => (
        <button
          key={index}
          type="button"
          className="aspect-video overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => onImageClick(index)}
          aria-label={image.alt ?? `Image ${index + 1}`}
        >
          <img src={image.src} alt={image.alt ?? ''} className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  )
}

function GalleryViewerThumbnails({
  images,
  activeIndex,
  onThumbnailClick,
}: {
  images: GalleryImage[]
  activeIndex: number
  onThumbnailClick: (index: number) => void
}) {
  return (
    <div data-slot="gallery-viewer-thumbnails" className="flex gap-1.5 overflow-x-auto scrollbar-none py-2 px-1">
      {images.map((image, index) => (
        <button
          key={index}
          type="button"
          onClick={() => onThumbnailClick(index)}
          aria-label={image.alt ?? `Thumbnail ${index + 1}`}
          aria-current={index === activeIndex ? 'true' : undefined}
          className={cn(
            'shrink-0 w-16 aspect-video cursor-pointer overflow-hidden rounded ring-offset-1',
            index === activeIndex && 'ring-2 ring-primary',
          )}
        >
          <img src={image.src} alt={image.alt ?? ''} className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  )
}

function GalleryViewerSingle({
  images,
  activeIndex,
  onPrev,
  onNext,
  onThumbnailClick,
}: {
  images: GalleryImage[]
  activeIndex: number
  onPrev: () => void
  onNext: () => void
  onThumbnailClick: (index: number) => void
}) {
  const activeImage = images[activeIndex]
  const hasPrev = activeIndex > 0
  const hasNext = activeIndex < images.length - 1

  return (
    <div data-slot="gallery-viewer-single">
      <div className="relative aspect-video bg-black overflow-hidden">
        {activeImage && (
          <img src={activeImage.src} alt={activeImage.alt ?? ''} className="w-full h-full object-cover" />
        )}
        {hasPrev && (
          <div className="absolute top-1/2 left-2 -translate-y-1/2">
            <Button variant="secondary" size="icon" onClick={onPrev} aria-label="Previous image">
              <ChevronLeftIcon />
            </Button>
          </div>
        )}
        {hasNext && (
          <div className="absolute top-1/2 right-2 -translate-y-1/2">
            <Button variant="secondary" size="icon" onClick={onNext} aria-label="Next image">
              <ChevronRightIcon />
            </Button>
          </div>
        )}
      </div>
      {/* DS: caption is secondary/supplementary text (text-sm text-muted-foreground). */}
      {activeImage?.caption && (
        <TextMuted block className="px-2 py-1.5">
          {activeImage.caption}
        </TextMuted>
      )}
      <GalleryViewerThumbnails images={images} activeIndex={activeIndex} onThumbnailClick={onThumbnailClick} />
    </div>
  )
}

function GalleryViewer({ images, defaultView = 'gallery', defaultCategory, className }: GalleryViewerProps) {
  const [view, setView] = React.useState<'gallery' | 'single'>(defaultView)
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [activeCategory, setActiveCategory] = React.useState<string | null>(defaultCategory ?? null)

  const categories = React.useMemo(() => {
    const seen = new Set<string>()
    for (const image of images) {
      if (image.category) seen.add(image.category)
    }
    return Array.from(seen)
  }, [images])

  const hasCategories = categories.length > 0

  const filteredImages = React.useMemo(() => {
    if (!activeCategory) return images
    return images.filter((image) => image.category === activeCategory)
  }, [images, activeCategory])

  function handleThumbnailClick(index: number) {
    setActiveIndex(index)
    setView('single')
  }

  function handleGridImageClick(index: number) {
    // index is relative to filteredImages — find the original image to get correct activeIndex
    const clickedImage = filteredImages[index]
    const originalIndex = images.indexOf(clickedImage)
    setActiveIndex(originalIndex !== -1 ? originalIndex : index)
    setView('single')
  }

  function handlePrev() {
    setActiveIndex((prev) => Math.max(0, prev - 1))
  }

  function handleNext() {
    setActiveIndex((prev) => Math.min(images.length - 1, prev + 1))
  }

  // For single view thumbnail strip, filter against the full images list
  // but keep activeIndex in sync with the global images array
  const singleViewThumbnails = React.useMemo(() => {
    if (!activeCategory) return images
    return images.filter((image) => image.category === activeCategory)
  }, [images, activeCategory])

  // The activeIndex within the thumbnail strip (filtered)
  const thumbnailActiveIndex = React.useMemo(() => {
    if (!activeCategory) return activeIndex
    const activeImage = images[activeIndex]
    const idx = singleViewThumbnails.indexOf(activeImage)
    return idx !== -1 ? idx : 0
  }, [images, activeIndex, activeCategory, singleViewThumbnails])

  function handleThumbnailStripClick(filteredIdx: number) {
    const clickedImage = singleViewThumbnails[filteredIdx]
    const originalIndex = images.indexOf(clickedImage)
    setActiveIndex(originalIndex !== -1 ? originalIndex : filteredIdx)
  }

  return (
    <div data-slot="gallery-viewer" className={cn('w-full rounded-lg overflow-hidden bg-black/5', className)}>
      {hasCategories && (
        <GalleryViewerFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />
      )}
      {view === 'gallery' ? (
        <GalleryViewerGrid images={filteredImages} onImageClick={handleGridImageClick} />
      ) : (
        <GalleryViewerSingle
          images={singleViewThumbnails}
          activeIndex={thumbnailActiveIndex}
          onPrev={handlePrev}
          onNext={handleNext}
          onThumbnailClick={handleThumbnailStripClick}
        />
      )}
    </div>
  )
}

export { GalleryViewer }
export type { GalleryViewerProps, GalleryImage }
